import React, { useCallback, useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import styled from 'styled-components';
import AddWorkspace from './AddWorkspace';
import CreateWorkspace from './CreateWorkspace';
import {
  CHART_NAME_MAX_LENGTH,
  DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
} from '../../../../config/constants';
import KubitModal from '../../../KubitModal';
import ChartFields from '../ChartFields';
import { useStrategyOptions } from '../../../../utils/hooks';
import {
  IWorkspaceAddAnalysesMutation,
  IWorkspaceAddAnalysesMutationVariables,
  IWorkspaceCreateMutation,
  IWorkspaceCreateMutationVariables,
  IWorkspaceListQuery,
  IWorkspaceListQueryVariables,
} from '../../../../utils/apollo/graphqlSchema';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  IWorkspaceItem,
  populateWorkspaces,
} from '../../../../redux/slices/workspaces';
import {
  WORKSPACE_ADD_ANALYSES,
  WORKSPACE_CREATE,
  WORKSPACES_LIST,
} from '../../../../utils/apollo/workspaces';
import { useNavigate } from 'react-router-dom';
import printGraphqlErrors from '../../../../utils/apollo/printGraphqlErrors';
import { PrimaryButton, SecondaryButton } from '../../../Button';
import {
  useValidateDescriptionField,
  useValidateNameField,
} from '../../../Form';
import { ModalActions } from '../../../KubitModal/KubitModalUI';
import RadioGroup from '../../../RadioGroup';
import toastMessage from '../../../../utils/toastMessage';
import { closeModal } from '../../../../redux/slices/modal';
import { useAnalytics } from '../../../../utils/analytics/useAnalytics';
import {
  workspaceCreated,
  workspaceUpdated,
} from '../../../../utils/analytics/events/workspace/events';
import { appRoutes } from '../../../../path';
import { usePrepareChartPrefs } from '../../hooks/useAnalysisSetChartPref';
import { useChartContext } from '../../ChartContext';
import { isNil } from 'lodash';
import { assertNotNil } from '../../../../utils/types';

interface IProps {
  analysisId: string;
}

const StrategyOptions = styled(RadioGroup)`
  margin-bottom: 24px;
`;

export default ({ analysisId }: IProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { track } = useAnalytics();
  const {
    metadata: { display, description },
  } = useChartContext();
  const preparePrefInput = usePrepareChartPrefs(analysisId);

  const handleClose = () => {
    dispatch(closeModal());
  };

  const loadWorkspacesInStore = useCallback(
    ({ workspaceList }: IWorkspaceListQuery) => {
      if (workspaceList?.entities) {
        dispatch(populateWorkspaces(workspaceList.entities));
      }
    },
    [dispatch],
  );
  const [searchString, setSearchString] = useState('');

  const { loading: isWorkspacesListLoading } = useQuery<
    IWorkspaceListQuery,
    IWorkspaceListQueryVariables
  >(WORKSPACES_LIST, {
    variables: {
      pagination: { after: 0, first: 100 },
      includes: [],
      term: searchString,
    },
    notifyOnNetworkStatusChange: true,
    onCompleted: loadWorkspacesInStore,
  });

  const workspaceList = useAppSelector(
    (state) => state.workspaces.workspaceList,
  );

  const [createWorkspace, { loading: createLoading }] = useMutation<
    IWorkspaceCreateMutation,
    IWorkspaceCreateMutationVariables
  >(WORKSPACE_CREATE);

  const [addAnalysisToWorkspace, { loading: addLoading }] = useMutation<
    IWorkspaceAddAnalysesMutation,
    IWorkspaceAddAnalysesMutationVariables
  >(WORKSPACE_ADD_ANALYSES);

  const { strategy, setStrategy, strategyOptions } = useStrategyOptions({
    defaultStrategy: 'update',
  });

  const handleChangeStrategy = (selectedStrategy: string) => {
    setWorkspaceName('');
    setSelectedWorkspace(null);
    setStrategy(selectedStrategy);
  };

  const [selectedWorkspace, setSelectedWorkspace] =
    useState<IWorkspaceItem | null>(null);

  const [workspaceName, setWorkspaceName] = useState('');
  const [workspaceDescription, setWorkspaceDescription] = useState('');

  const [analysisName, setAnalysisName] = useState(display);
  const [analysisDescription, setAnalysisDescription] = useState(description);

  const isWorkspaceSelectionValid = !isNil(selectedWorkspace);

  const { isValid: isValidWorkspaceName } = useValidateNameField({
    draftName: workspaceName,
    limit: DASHBOARD_WORKSPACE_NAME_MAX_LENGTH,
    originalName: '',
  });

  const { isValid: isValidWorkspaceDescription } = useValidateDescriptionField({
    draftValue: workspaceDescription,
    originalValue: '',
  });

  const { isValid: isValidAnalysisName } = useValidateNameField({
    draftName: analysisName,
    limit: CHART_NAME_MAX_LENGTH,
    originalName: display,
  });

  const { isValid: isValidAnalysisDescription } = useValidateDescriptionField({
    draftValue: analysisDescription,
    originalValue: description,
  });

  const isCreateFormValid =
    isValidWorkspaceName &&
    isValidWorkspaceDescription &&
    isValidAnalysisName &&
    isValidAnalysisDescription;

  const isAddFormValid =
    isWorkspaceSelectionValid &&
    isValidAnalysisName &&
    isValidAnalysisDescription;

  const handleCreate = (shouldRedirect?: boolean) => async () => {
    const { pref } = preparePrefInput();

    const result = await createWorkspace({
      variables: {
        input: {
          name: workspaceName.trim(),
          description: workspaceDescription.trim(),
          comment: '',
          analyses: [
            {
              analysisId,
              display: analysisName,
              description: analysisDescription,
              pref,
            },
          ],
        },
      },
    });

    const errors = result?.data?.workspaceCreate?.errors;

    if (printGraphqlErrors(errors)) {
      return;
    }

    const entity = result?.data?.workspaceCreate?.entity;

    if (entity) {
      track(workspaceCreated(entity));
      handleClose();
      const redirectUrl = appRoutes.workspaces.workspace(entity.id);
      toastMessage({
        message: `Workspace ${entity.name} has been created.`,
        type: shouldRedirect ? 'info' : 'action',
        redirectPath: shouldRedirect ? undefined : redirectUrl,
      });

      if (shouldRedirect) {
        navigate(redirectUrl);
      }
    }
  };

  const handleAdd = (shouldRedirect?: boolean) => async () => {
    assertNotNil(selectedWorkspace);
    const { pref } = preparePrefInput();
    const result = await addAnalysisToWorkspace({
      variables: {
        input: {
          wsId: selectedWorkspace.id,
          analyses: [
            {
              analysisId,
              display: analysisName,
              description: analysisDescription,
              pref,
            },
          ],
        },
      },
    });

    const errors = result?.data?.wsAddAnalyses?.errors;

    if (printGraphqlErrors(errors)) {
      return;
    }

    const entity = result.data?.wsAddAnalyses.entity;

    if (entity) {
      track(workspaceUpdated(entity));
      handleClose();

      const lastAnalyses = entity.rows[entity.rows.length - 1]?.analyses;
      const newAnalysis = lastAnalyses?.[lastAnalyses?.length - 1];

      const redirectUrl = appRoutes.workspaces.workspace(entity.id);

      if (newAnalysis) {
        toastMessage({
          message: `Report added to workspace ${entity.name}.`,
          type: shouldRedirect ? 'info' : 'action',
          redirectPath: shouldRedirect ? undefined : redirectUrl,
        });

        if (shouldRedirect) {
          navigate(redirectUrl);
        }
      }
    }
  };

  return (
    <KubitModal header={'Add to Workspace'} onClose={handleClose} open>
      <StrategyOptions
        name="strategy"
        value={strategy}
        onChange={handleChangeStrategy}
        options={strategyOptions}
      />
      {strategy === 'create' ? (
        <CreateWorkspace
          workspaceName={workspaceName}
          setWorkspaceName={setWorkspaceName}
          workspaceDescription={workspaceDescription}
          setWorkspaceDescription={setWorkspaceDescription}
        />
      ) : (
        <AddWorkspace
          availableWorkspaces={workspaceList}
          onWorkspaceSelect={(workspace) => {
            setSelectedWorkspace(workspace);
            setWorkspaceName(workspace?.name ?? '');
          }}
          isWorkspacesListLoading={isWorkspacesListLoading}
          searchString={searchString}
          onSearchChange={setSearchString}
        />
      )}
      <ChartFields
        analysisName={analysisName}
        setAnalysisName={setAnalysisName}
        analysisDescription={analysisDescription}
        setAnalysisDescription={setAnalysisDescription}
      />
      <ModalActions>
        <SecondaryButton
          type="button"
          onClick={handleClose}
          trackContext="reports"
          trackAction="add to workspace - cancel"
        >
          Cancel
        </SecondaryButton>
        {strategy === 'create' ? (
          <>
            <PrimaryButton
              type="button"
              onClick={handleCreate(true)}
              isGreyedOut={!isCreateFormValid}
              loading={createLoading}
              disabled={!isCreateFormValid || createLoading}
              trackContext="reports"
              trackAction="add to workspace - create & go"
            >
              Create & Go
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={handleCreate()}
              isGreyedOut={!isCreateFormValid}
              loading={createLoading}
              disabled={!isCreateFormValid || createLoading}
              trackContext="reports"
              trackAction="add to workspace - create"
            >
              Create
            </PrimaryButton>
          </>
        ) : (
          <>
            <PrimaryButton
              type="button"
              onClick={handleAdd(true)}
              isGreyedOut={!isAddFormValid}
              loading={addLoading}
              disabled={!isAddFormValid || addLoading}
              trackContext="reports"
              trackAction="add to workspace - add & go"
            >
              Add & Go
            </PrimaryButton>
            <PrimaryButton
              type="button"
              onClick={handleAdd()}
              isGreyedOut={!isAddFormValid}
              loading={addLoading}
              disabled={!isAddFormValid || addLoading}
              trackContext="reports"
              trackAction="add to workspace - add"
            >
              Add
            </PrimaryButton>
          </>
        )}
      </ModalActions>
    </KubitModal>
  );
};
